import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Create,
    GetAllPoolName,
    InhouseGuest,
    GetAllOldCustomer
} from "adminApi/PoolBookingApi/PoolBooking.js";
import { validateForm } from "utils/validation";
import { PoolBookingRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import fields from "../json/PoolBookingFields.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import AddPoolBookingForm from "./components/AddPoolBookingForm";
import useFetchAllData from "Hooks/useFetchAllData";

const AddPoolBooking = () => {
    const navigate = useNavigate();
    const [CustomerOpctions, setCustomerOpctions] = useState([]);

    const extraData = [
        { "name": "from_date" },
        { "name": "to_date" },
        { "name": "customer_type" }
    ];

    const { formdata, newcustomer } = fields;

    const allFields = useMemo(
        () => [...formdata, ...newcustomer, ...extraData],
        []
    );

    const initialValues = useMemo(() => {
        return allFields.reduce((acc, field) => {
            acc[field.name] = "";
            return acc;
        }, {});
    }, [allFields]);

    const {
        values,
        errors,
        handleChange,
        setErrors,
        setValues
    } = useFormChange(initialValues, PoolBookingRules);

    const { data: PooNameOptions = [] } = useFetchAllData(GetAllPoolName);
    const { data: OldCustomerOptions = [] } = useFetchAllData(GetAllOldCustomer);

    /* ---------- INHOUSE GUEST ---------- */
    const getInhouseGuest = useCallback(async ({ from_date, to_date }) => {
        try {
            const res = await InhouseGuest({ data: { from_date, to_date } });
            const data = res?.data?.data?.data || [];
            setCustomerOpctions(data);
        } catch (err) {
            console.error(err);
        }
    }, []);

    /* ---------- PACKAGE CHANGE ---------- */
    useEffect(() => {
        if (!values.p_pack_id) return;

        const pkg = PooNameOptions.find(
            p => p.pool_package_id === Number(values.p_pack_id)
        );
        if (!pkg) return;

        setValues(prev => ({
            ...prev,
            price: pkg.price,
            from_date: pkg.from_date,
            to_date: pkg.to_date
        }));

        if (pkg.from_date && pkg.to_date) {
            getInhouseGuest(pkg);
        }
    }, [values.p_pack_id, PooNameOptions, setValues, getInhouseGuest]);

    /* ---------- SAVE CUSTOMER FROM MODAL ---------- */
    const handleCustomerSave = (customer) => {
        const normalizedCustomer = {
            customer_id: customer.customer_id || Date.now(),
            customer_name:
                customer.customer_name ||
                `${customer.first_name ?? ""} ${customer.last_name ?? ""}`.trim(),
            customer_type: customer.customer_type
        };

        setValues(prev => ({
            ...prev,
            customer_id: normalizedCustomer.customer_id,
            customer_name: normalizedCustomer.customer_name,
            customer_type: normalizedCustomer.customer_type,
            ...(customer.customer_type === "new_customer" && {
                first_name: customer.first_name,
                last_name: customer.last_name,
                email: customer.email,
                customer_number: customer.customer_number,
                pid_type: customer.pid_type,
                pid: customer.pid,
                country_dial_code: customer.country_dial_code,
                new_customer: true
            })
        }));

        setCustomerOpctions(prev => {
            const exists = prev.some(
                c => c.customer_id === normalizedCustomer.customer_id
            );
            return exists ? prev : [...prev, normalizedCustomer];
        });
    };

    /* ---------- FORM SUBMIT ---------- */
    const { loading, handleSubmit } = useFormSubmit(
        async (formValues) => {
            // Copy before mutating — never mutate the original formValues object
            const payload = { ...formValues };
            delete payload.to_date;
            delete payload.from_date;
            delete payload.customer_type;
            // amount = price (single unit price × units is computed server-side)
            payload.amount = Number(values.price);
            if (payload.new_customer) {
                delete payload.customer_id;
            } else {
                payload.new_customer = false;
            }
            return await Create({ data: payload });
        },
        (vals) => {
            const err = validateForm(vals, PoolBookingRules);
            setErrors(err);
            return err;
        },
        () => navigate(-1),
        "Pool Booking Created Successfully"
    );

    return (
        <>
            {loading && <Spinner isSpinner />}

            <AddPoolBookingForm
                fields={formdata}
                values={values}
                errors={errors}
                handleChange={handleChange}
                handleSubmit={(e) => handleSubmit(e, values)}
                loading={loading}
                PooNameOptions={PooNameOptions}
                OldCustomerOptions={OldCustomerOptions}
                newcustomer={newcustomer}
                CustomerOpctions={CustomerOpctions}
                onCustomerSave={handleCustomerSave}
            />
        </>
    );
};

export default AddPoolBooking;
